import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, LinearProgress, Alert, Typography } from '@mui/material';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  FileGrid,
  FileContextMenu,
  FileFlightDetailsDialog,
  CreateFolderDialog,
  RenameFolderDialog,
  FileUploadZone,
  FileBreadcrumbs,
  useFileOperations,
  useFileNavigation,
  useFileSelection,
  useFileClipboard,
  useBrowserShortcuts,
  useBulkDownload,
  BulkDownloadStatusLine,
  downloadTargets,
  canMoveInto,
  folderRefForEntry,
  shareTargets,
  entryId,
  entryIds,
  entriesForAction,
  selectedEntries,
  selectionAfterMove,
  DragCountChip,
  FileBrowserProps,
  FileItem,
} from './file-browser';
import type { Crumb } from './file-browser/navigationModel';
import { ObjectShareDialog } from './object-shares';
import { LinkBinToFlightDialog } from './flight-links';
import { useGetDownloadUrlMutation } from './fileManagement.queries';
import { useAuthV2 } from '../hooks/useAuthV2';
import { isAdmin as checkIsAdmin } from '../config/roles';
import { useTranslation } from '../i18n';

const FileBrowser: React.FC<FileBrowserProps> = ({
  files,
  loading,
  error,
  currentFolder,
  crumbs,
  insideShare,
  currentPath,
  total,
  pageCount,
  onBackToShared,
  onNavigate,
  onDownload,
  onDelete,
  onMoveMany,
  onRenameFolder,
  onCreateFolder,
  onUpload,
  onAnalyze,
  uploadLoading,
  analysisResult,
  onCloseAnalysis,
  onNavigateToDocuments,
  onNavigateToReports,
  onNavigateToShared,
  onFlightUpdated,
  fileFilters,
  serialNumbers,
  onFileFilterChange,
  onResetFileFilters,
}) => {
  const [showUploadZone, setShowUploadZone] = useState<boolean>(false);
  // File whose linked flight is shown in the editable Flight Details dialog.
  const [detailsFile, setDetailsFile] = useState<FileItem | null>(null);
  // Files and/or FOLDERS whose registry rows are being shared / unshared
  // (#1182 Stage 4 slice 4) — the one Share action since B-6 (STD-029 R-12).
  // A list, because the tab is multi-select: the first version took one item
  // and silently shared only the right-clicked file out of a selection.
  const [shareObjectFiles, setShareObjectFiles] = useState<FileItem[]>([]);
  // The .bin log being linked to its flight record (#1694).
  const [linkFlightFile, setLinkFlightFile] = useState<FileItem | null>(null);
  const { t } = useTranslation();

  // Two capabilities, deliberately not one boolean serving both jobs.
  //
  // SEEING what has been shared is plain authenticated read. Neither `GET
  // /api/flight-shares` (flights) nor `GET /api/file-objects/shared` (files and
  // folders, #1205) is role-gated, and RLS already limits the rows to what the
  // caller's organization is a party to. ADMINISTERING them — granting and
  // revoking — is `require_org_admin`, so a member is not offered those menu
  // items at all rather than shown ones that 403.
  //
  // `canViewShares` gates ONE entry point since #1242 — the two shared screens
  // became one view with a Flights/Files toggle.
  //
  // Conflating the two is what made the "shared with me" half of this feature
  // unreachable: gating *visibility* on an *authority* check hid the listing
  // from every recipient it was built for (#1206 — all 48 production flight
  // shares, invisible to everyone who was not an admin).
  //
  // So: `canViewShares` gates the ENTRY POINT below; `canAdministerShares`
  // gates the SHARE ACTION — singular on both counts since B-6 collapsed the
  // menu's two grant items into one (STD-029 R-12). If you find yourself passing
  // `canAdministerShares` to a `onNavigateTo*` prop, stop — that is the bug,
  // and `canViewShares` in `shared-view/sharedViewModel.ts` has the long-form
  // argument plus the test that fails when it is reintroduced.
  const { profile } = useAuthV2();
  const canViewShares = !!profile;
  const canAdministerShares = checkIsAdmin(profile?.organization?.roles);

  const navigation = useFileNavigation({ onNavigate });
  const selection = useFileSelection();
  const clipboard = useFileClipboard();

  // `files` arrives filtered, aggregated and SORTED by the server (B-4). The
  // client-side `useFileProcessing` that used to do all three is deleted, not
  // dormant: its folder synthesis, its `deriveFolderMetadata` and its
  // `compareItems` are `packages/file_registry/children.py` now, where they can
  // be tested exhaustively and where they can see the whole subtree rather than
  // whatever slice a flat projection happened to send.

  // Declared after `selection`/`files` because the share handler closes over
  // both. `shareTargets` is the pure rule (and is unit-tested).
  const fileOperations = useFileOperations({
    currentFolder,
    currentPath,
    onDownload,
    onDelete,
    onRenameFolder,
    onCreateFolder,
    onUpload,
    onShowDetails: setDetailsFile,
    onShareObject: (file) => setShareObjectFiles(shareTargets(file, selection.selectedIds, files)),
    onLinkFlight: setLinkFlightFile,
  });

  // Ordered visible entry ids back the Shift-range selection (STD-029 R-2).
  const orderedIds = useMemo(() => entryIds(files), [files]);

  // Selection is per-directory (Drive/Explorer): clear it whenever we navigate.
  // Keyed on the node id now, not the path — the identity that survives a
  // rename, and the only one a shared folder has (R-10).
  const { clear: clearSelection } = selection;
  useEffect(() => {
    clearSelection();
  }, [currentFolder.id, clearSelection]);

  // Keyboard shortcuts (Drive/Explorer): Ctrl/Cmd+A select-all, +X cut,
  // +V paste-as-move-into-current-dir, Escape clear (clipboard first, else
  // selection). Suppressed while typing in an input/textarea/contenteditable —
  // `useBrowserShortcuts`, which the Shared view listens through too (#1692).
  useBrowserShortcuts({
    selectAll: () => selection.selectAllIds(orderedIds),
    cut: () => {
      const items = selectedEntries(files, selection.selectedIds);
      // The ORIGIN rides along. See `useFileClipboard.cutFrom`: without it the
      // paste site has nothing honest to compare against, and the placeholder
      // it used instead (`{id: null}`) silently aliased the organization root.
      if (items.length) clipboard.cut(items, currentFolder);
    },
    paste: () => {
      const items = clipboard.items;
      // ONE condition. It used to be two — "would this create a cycle / is it
      // a no-op" plus "do we hold an id to send" — and the second stopped
      // existing when every destination became a real node (B-4).
      //
      // `clipboard.cutFrom` is the folder the items were cut from, recorded at
      // cut time because the clipboard survives navigation and nothing here
      // could recover it afterwards. Passing a literal `{id: null}` instead —
      // which the first cut of B-4 did — made every paste AT THE ROOT compare
      // `null === null`, trip the no-op guard, and do nothing at all: no
      // request, no error, no toast. Cut/paste up to the root is the
      // documented workaround for half this browser's gestures, so that was
      // both silent and on the main path.
      if (items.length && canMoveInto([...items], currentFolder, clipboard.cutFrom)) {
        // Only clear the clipboard if every item moved; on partial/total
        // failure keep it so the user can retry (mirrors the drag path).
        void onMoveMany([...items], currentFolder).then((failedIds) => {
          if (failedIds.length === 0) clipboard.clear();
        });
      }
    },
    clear: () => {
      if (clipboard.hasClipboard) clipboard.clear();
      else selection.clear();
    },
  });

  // "Download (N files)" (#1692) — the run the Shared view uses too. Presigns
  // the way `FileManagement.handleDownload` does: by row id, or by the path for
  // an R-9 entry with no row. A second mutation instance, so one failed file
  // is reported in the run's outcome line rather than as a page error.
  const bulkPresignMutation = useGetDownloadUrlMutation();
  const presignBulk = useCallback(
    async (entry: FileItem) =>
      (
        await bulkPresignMutation.mutateAsync({
          objectId: entry.object_id,
          fileKey: entry.downloadPath ?? '',
        })
      ).download_url,
    [bulkPresignMutation]
  );
  const nameOfFile = useCallback((entry: FileItem) => entry.name, []);
  const bulkDownload = useBulkDownload(presignBulk, nameOfFile);

  const menuDownloadTargets = fileOperations.selectedFile
    ? downloadTargets(fileOperations.selectedFile, selection.selectedIds, files)
    : [];

  // One file the way it always went (a new tab the browser turns into a
  // download); several through the bulk run.
  const handleMenuDownload = () => {
    if (menuDownloadTargets.length > 1) {
      bulkDownload.start(menuDownloadTargets);
      fileOperations.closeMenu();
    } else {
      void fileOperations.handleDownload();
    }
  };

  // Single-click selects (honouring Ctrl/Cmd/Shift); double-click opens.
  const handleItemClick = (file: FileItem, event: React.MouseEvent<HTMLElement>) => {
    selection.selectOnClick(
      entryId(file),
      { ctrlOrMeta: event.ctrlKey || event.metaKey, shift: event.shiftKey },
      orderedIds
    );
  };

  const handleItemOpen = (file: FileItem) => {
    if (file.isFolder) {
      navigation.navigateToFile(file);
    } else {
      void onDownload(file);
    }
  };

  // Explorer-style right-click: if the item isn't already selected, select just it.
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>, file: FileItem) => {
    if (!selection.isSelected(entryId(file))) {
      selection.replaceWith(entryId(file));
    }
    fileOperations.handleContextMenu(event, file);
  };

  // --- Drag-and-drop bulk move (dnd-kit) ---
  const [activeDragItems, setActiveDragItems] = useState<FileItem[]>([]);
  const sensors = useSensors(
    // Distance constraint so a plain click still selects (drag only after moving).
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    // The dragged ENTRY comes off `data`, never off `active.id` — the id is an
    // opaque identity (STD-029 R-2) and nothing here may parse it.
    const dragged = event.active.data.current?.file as FileItem | undefined;
    if (!dragged) return;
    const draggedId = entryId(dragged);
    // Drag the whole selection if the grabbed card is part of it; otherwise just
    // it — `entriesForAction` is that rule, shared with `shareTargets` so the
    // two cannot drift and so a revert to path-keying fails a unit test.
    const movingItems = entriesForAction(dragged, selection.selectedIds, files);
    if (!selection.isSelected(draggedId)) {
      selection.replaceWith(draggedId);
    }
    setActiveDragItems(movingItems);
  };

  const handleDragCancel = () => setActiveDragItems([]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const items = activeDragItems;
    setActiveDragItems([]);
    const over = event.over;
    if (!over || items.length === 0) return;

    // Resolve the destination from the drop target's DATA — the folder entry a
    // card carries, or the crumb a breadcrumb carries. Both are NODES now, so
    // there is no path math left on this path at all.
    const data = over.data.current as { file?: FileItem; crumb?: Crumb } | undefined;
    const target = data?.file ? folderRefForEntry(data.file) : data?.crumb?.ref;
    if (!target) return;

    // One guard, and it is id math (see `moveGuards`): a cycle is structurally
    // unreachable in a one-level listing, so what is left is "not onto yourself"
    // and "not a no-op".
    if (!canMoveInto(items, target, currentFolder)) return;

    // Keep failed items selected so the user can retry; clear on full success.
    //
    // `onMoveMany` answers in entry IDS, not display paths. It answered in paths
    // in B-1's first cut, and that re-selected BOTH rows of a dual-entry path
    // when only one of them failed — telling the user to retry a move that had
    // already succeeded, which is the exact ambiguity this arc exists to end.
    // The address that goes out is the address that comes back.
    const failedIds = await onMoveMany(items, target);
    selection.setSelected(selectionAfterMove(items, failedIds));
  };

  // A folder card is a valid drop target when the guard allows the current drag.
  // The second question this used to ask — "do we hold an address for it?" —
  // is gone: every folder in a listing is a registry row (B-4).
  const canDropOnFolder = useCallback(
    (folder: FileItem) =>
      activeDragItems.length > 0 &&
      canMoveInto(activeDragItems, folderRefForEntry(folder), currentFolder),
    [activeDragItems, currentFolder]
  );

  // The breadcrumb's version, and **every crumb can now answer yes** — which is
  // D-4c closing. The flat projection carried no ancestor ids, so an ancestor
  // crumb could be named and not addressed, and the browser refused the drop
  // rather than send a `parent_id` it did not have. The children listing carries
  // the ancestor chain, so it does.
  const canDropOnCrumb = useCallback(
    (crumb: Crumb) =>
      activeDragItems.length > 0 && canMoveInto(activeDragItems, crumb.ref, currentFolder),
    [activeDragItems, currentFolder]
  );

  const handleToggleUploadZone = () => {
    setShowUploadZone(!showUploadZone);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Breadcrumbs */}
        <FileBreadcrumbs
          crumbs={crumbs}
          insideShare={insideShare}
          onBackToShared={onBackToShared}
          showUploadZone={showUploadZone}
          onNavigateToCrumb={(crumb) => navigation.navigateTo(crumb.ref)}
          // Offered inside a share too since #1351 — see the upload zone below.
          onToggleUploadZone={handleToggleUploadZone}
          onNavigateToDocuments={onNavigateToDocuments}
          onNavigateToReports={onNavigateToReports}
          onNavigateToShared={canViewShares ? onNavigateToShared : undefined}
          canDropOnCrumb={canDropOnCrumb}
          // Always offered since B-4: every destination is addressable, and the
          // root is `parent_id: null`, which the API resolves to the caller's
          // own root. The withheld case was a folder synthesized from a flight's
          // path, and nothing synthesizes folders any more.
          onOpenCreateFolder={fileOperations.openCreateFolderDialog}
          fileFilters={fileFilters}
          serialNumbers={serialNumbers}
          onFileFilterChange={onFileFilterChange}
          onResetFileFilters={onResetFileFilters}
        />

        {/* Upload Zone — offered inside a folder another organization shared
            as well, since #1351. Inside a share the upload goes to the
            id-addressed `POST /api/file-objects/{parent_id}/upload` (see
            `FileManagement.handleUpload`), which now runs the same flight
            ingest as `/api/files/upload`, so a CSV dropped here is a flight
            and not a flight-shaped file. What it refuses — and says so, as
            `FILE_OBJECT_FLIGHT_LOG_FOREIGN_FOLDER` — is a flight log into a
            folder ANOTHER organization owns: that flight would be theirs and
            only they can create it, so the log belongs in the uploader's own
            Files, shared from there. Documents land as before. Like the
            create-folder button, the zone is offered regardless of the share's
            permission; a read-only share answers 403 and the uploader shows it. */}
        <FileUploadZone
          show={showUploadZone}
          currentPath={currentPath}
          onUpload={fileOperations.handleUploadWithPath}
          onAnalyze={onAnalyze}
          uploadLoading={uploadLoading}
          analysisResult={analysisResult}
          onCloseAnalysis={onCloseAnalysis}
        />

        <BulkDownloadStatusLine status={bulkDownload.status} onDismiss={bulkDownload.dismiss} />

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Files Grid */}
        <FileGrid
          files={files}
          loading={loading}
          selectedIds={selection.selectedIds}
          cutIds={clipboard.cutIds}
          onItemClick={handleItemClick}
          onItemOpen={handleItemOpen}
          onBackgroundClick={selection.clear}
          onMarqueeSelect={(ids) => selection.setSelected(new Set(ids))}
          canDropOnFolder={canDropOnFolder}
          onContextMenu={handleContextMenu}
        />

        {/* Growth past the page bound is visible rather than silent ("no silent
            caps"). There is deliberately no pager control: prod's largest folder
            holds 178 children against a default of 500, so a control here would
            be unreachable and untested.

            Compared against `pageCount` — the SERVER's page — and not against
            the rendered list, which also holds the R-9 `unregistered` arm that
            `total` counts in neither. Comparing against the render under-reports
            by however many unregistered entries are on screen, and at the root
            (where that arm lives) it can suppress the notice outright. */}
        {total > pageCount && (
          <Box sx={{ mt: 1, textAlign: 'center' }} data-testid="children-page-notice">
            <Typography variant="caption" color="textSecondary">
              {t('fileManagement.showingOfTotal', { shown: pageCount, total })}
            </Typography>
          </Box>
        )}

        <DragOverlay dropAnimation={null}>
          {activeDragItems.length > 0 ? (
            <DragCountChip>
              {activeDragItems.length === 1
                ? activeDragItems[0].name
                : `${activeDragItems.length} items`}
            </DragCountChip>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Context Menu */}
      <FileContextMenu
        anchorEl={fileOperations.anchorEl}
        selectedFile={fileOperations.selectedFile}
        onClose={fileOperations.closeMenu}
        onDownload={handleMenuDownload}
        downloadTargetCount={menuDownloadTargets.length}
        onRename={fileOperations.handleRename}
        onDelete={fileOperations.handleDelete}
        onPlot={fileOperations.handlePlot}
        onDetails={fileOperations.handleShowDetails}
        onShareObject={canAdministerShares ? fileOperations.handleShareObject : undefined}
        onLinkFlight={fileOperations.handleLinkFlight}
        // Resolved through the same rule the action itself uses, so the label
        // and the outcome cannot disagree.
        shareTargetCount={
          fileOperations.selectedFile
            ? shareTargets(fileOperations.selectedFile, selection.selectedIds, files).length
            : 0
        }
      />

      {/* Editable Flight Details dialog (files linked to a flight) */}
      <FileFlightDetailsDialog
        file={detailsFile}
        onClose={() => setDetailsFile(null)}
        onFlightUpdated={onFlightUpdated}
      />

      {/* Share / unshare the file or folder itself (#1182 Stage 4 slice 4) —
          the file browser's ONE share dialog since B-6 (STD-029 R-12). The
          label is the row's own `name`; nothing slices a path any more. */}
      <ObjectShareDialog
        objectIds={shareObjectFiles.map((f) => f.object_id).filter((id): id is string => !!id)}
        label={shareObjectFiles.length === 1 ? shareObjectFiles[0].name : undefined}
        // Any folder in the selection means the "covers files added later" note
        // applies to at least one of them, and that is the surprising half —
        // withholding it because the selection is mixed would be the wrong way
        // to be wrong.
        isFolder={shareObjectFiles.some((f) => f.isFolder)}
        onClose={() => setShareObjectFiles([])}
      />

      {/* Link a .bin log to its flight record (#1694) */}
      <LinkBinToFlightDialog
        objectId={linkFlightFile?.object_id}
        label={linkFlightFile?.name}
        onClose={() => setLinkFlightFile(null)}
      />

      {/* Create Folder Dialog */}
      <CreateFolderDialog
        open={fileOperations.showCreateFolder}
        newFolderName={fileOperations.newFolderName}
        onClose={fileOperations.closeCreateFolderDialog}
        onConfirm={fileOperations.handleCreateFolder}
        onNameChange={fileOperations.setNewFolderName}
      />

      {/* Rename Folder Dialog */}
      <RenameFolderDialog
        open={fileOperations.showRenameDialog}
        currentName={fileOperations.renameTarget?.name ?? ''}
        onClose={fileOperations.closeRenameDialog}
        onConfirm={fileOperations.handleRenameConfirm}
      />
    </Box>
  );
};

export default FileBrowser;
